import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { useAuth } from "@/contexts/AuthContext";

// Destino do link do e-mail de cadastro: confirma o e-mail, abre a sessão e
// cai na home — o usuário nunca digita código.
//
// Por que o consumo do token acontece AQUI (POST via JS) e não num GET direto
// na API: scanners de link de e-mail (Outlook Safe Links, antivírus) buscam a
// URL sem executar JavaScript. Um GET que confirmasse a conta queimaria o
// token de uso único antes do clique real do usuário.
export default function VerificarEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // StrictMode monta o efeito duas vezes em dev; o token é de uso único, então
  // a segunda chamada responderia "link inválido" e mostraria erro na cara do
  // usuário. Este guard garante uma única troca por carregamento.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    if (!token) {
      setError("Link inválido. Abra o link exatamente como está no e-mail.");
      return;
    }

    let active = true;
    void verifyEmail(token).then((result) => {
      if (!active) return;
      if (result.error) {
        setError(result.error);
        setExpired(result.expired === true);
        return;
      }
      navigate("/", { replace: true });
    });
    return () => {
      active = false;
    };
  }, [token, verifyEmail, navigate]);

  const onResend = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Informe o e-mail do cadastro.");
      return;
    }
    setResending(true);
    const result = await resendVerification(email.trim());
    setResending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setError(null);
    setResent(true);
  };

  if (!error) {
    return (
      <AuthLayout
        title="Confirmando…"
        subtitle="Só um instante — estamos confirmando seu e-mail e abrindo sua sessão."
      >
        <div className="flex justify-center py-6">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Link não confirmado"
      subtitle={
        expired
          ? "Seu link de confirmação expirou. Enviamos links válidos por 30 minutos."
          : "Não foi possível confirmar seu e-mail com este link."
      }
    >
      <p className="rounded-(--radius-field) bg-destructive/15 px-4 py-3 text-sm text-[#ffb4ad]">
        {error}
      </p>

      {resent ? (
        <p className="mt-4 rounded-(--radius-field) bg-accent/15 px-4 py-3 text-sm text-accent">
          Se este e-mail tem uma conta pendente de confirmação, um novo link
          acabou de sair. Confira sua caixa de entrada.
        </p>
      ) : (
        <form onSubmit={onResend} className="mt-6 flex flex-col gap-4.5">
          <Field
            label="E-mail do cadastro"
            tone="auth"
            type="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={resending}
          />
          <Button
            type="submit"
            title="Reenviar link"
            loading={resending}
            className="mt-1"
          />
        </form>
      )}

      <div className="mt-6 flex justify-center">
        <Link
          to="/login"
          className="text-[13px] font-bold text-surface-alt-foreground/50"
        >
          Voltar para o login
        </Link>
      </div>
    </AuthLayout>
  );
}
